'use client'

/**
 * TheScannerRead — "the read on $X" from the SCANNER's angle, on /stocks/[symbol]. Built for the
 * demand measured in the social signals: people reply to our X/Reddit posts asking "what's the read
 * on $TICKER?". The stock page already shows price (StockPriceHero) and WHY it is moving
 * (WhyMovingHeadline); this adds the piece neither does - the scanner's own TRACK on the name: how
 * unusual the volume is, how many times and how recently we have flagged it, and what kind of mover
 * it is. That is the caught-early value prop, surfaced exactly where someone researching the ticker
 * lands, and it makes every /stocks/$X link a shareable answer to post back to a social asker.
 *
 * ⭐ HONEST BY CONSTRUCTION. Every line is the scanner's real record from getWhyMoving; there is no
 * prediction and no "buy/sell" - the framing says so, per the owner rule that the top-6 posts get
 * misread as calls. Hides entirely for a ticker the scanner has not flagged (qualifiedRecently false):
 * a "we have never flagged this" card is not a read worth showing.
 */

import { useEffect, useState, type ReactNode } from 'react'
import Link from 'next/link'
import { Radar, Activity, Flame, PauseCircle, ArrowRight } from 'lucide-react'

type WhyAlert = { kind: string; at: string }
type Why = {
  symbol: string
  movePct: number | null
  direction: 'up' | 'down' | 'moving'
  rvolX: number | null
  halted: boolean
  alertCount: number
  qualifiedRecently: boolean
  alertCount30d: number
  alertKinds30d: string[]
  lastAlertAt: string | null
  alerts: WhyAlert[]
}

function relDays(iso: string | null): string | null {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  const days = Math.floor((Date.now() - then) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  return `${days} days ago`
}

export default function TheScannerRead({ symbol }: { symbol: string }) {
  const [w, setW] = useState<Why | null>(null)

  useEffect(() => {
    let alive = true
    fetch(`/api/stocks/why?symbol=${encodeURIComponent(symbol)}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive && j && typeof j === 'object') setW(j as Why) })
      .catch(() => {})
    return () => { alive = false }
  }, [symbol])

  // Show only when the scanner actually has a read on this name.
  if (!w || !w.qualifiedRecently) return null

  const rvol = w.rvolX && w.rvolX >= 1.5 ? `${w.rvolX.toFixed(1)}×` : null
  const firstToday = w.alertCount > 0 ? w.alerts.find((a) => a.kind && !a.kind.startsWith('ema'))?.at ?? null : null
  const last = relDays(w.lastAlertAt)
  const kinds = (w.alertKinds30d || []).slice(0, 3).join(', ')

  const rows: { Icon: typeof Activity; label: string; body: ReactNode }[] = []
  if (rvol) {
    rows.push({ Icon: Activity, label: 'Unusual volume', body: <>Trading at <b className="text-foreground">{rvol}</b> its average volume, the flag that put it on the scanner.</> })
  }
  if (w.alertCount > 0) {
    rows.push({ Icon: Radar, label: 'Flagged today', body: <><b className="text-foreground">{w.alertCount}</b> alert{w.alertCount === 1 ? '' : 's'} today{firstToday ? <>, first at <b className="text-foreground">{firstToday} ET</b></> : null}.</> })
  } else if (w.alertCount30d > 0) {
    rows.push({ Icon: Radar, label: 'On our radar', body: <>Flagged <b className="text-foreground">{w.alertCount30d}</b> time{w.alertCount30d === 1 ? '' : 's'} in the last 30 days{last ? <>, last <b className="text-foreground">{last}</b></> : null}.</> })
  }
  if (w.halted) {
    rows.push({ Icon: PauseCircle, label: 'Halted', body: <>Hit a <b className="text-foreground">trading halt</b> today, the profile that runs fast and reverses fast.</> })
  }
  if (kinds) {
    rows.push({ Icon: Flame, label: 'What kind of mover', body: <>Our alerts on it: <span className="text-foreground/80">{kinds}</span>.</> })
  }
  if (!rows.length) return null

  return (
    <section className="mb-5 overflow-hidden rounded-2xl border border-amber-500/20 bg-[linear-gradient(180deg,rgba(251,191,36,0.05),transparent_55%)] bg-card">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <Radar className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <h2 className="text-sm font-bold tracking-tight text-foreground">The scanner&apos;s read on ${symbol}</h2>
      </div>
      <div className="px-4 py-1">
        {rows.map((r, i) => (
          <div key={i} className="flex gap-3 border-t border-border py-2.5 first:border-t-0">
            <r.Icon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600/90 dark:text-amber-400/90" />
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-foreground">{r.label}</div>
              <div className="text-[13px] leading-snug text-muted-foreground">{r.body}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border px-4 py-3">
        <Link prefetch={false} href="/alerts" className="group inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-b from-amber-300 to-amber-500 px-3.5 py-2 text-[13px] font-bold text-black transition-all hover:from-amber-200 hover:to-amber-400">
          Get live alerts on ${symbol}
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </Link>
        <Link rel="nofollow" href={`/chart/${encodeURIComponent(symbol)}`} className="text-[13px] font-semibold text-foreground/80 hover:text-foreground">
          See it on the chart
        </Link>
        <span className="text-[11px] text-muted-foreground">The setup and the data, not a prediction. Not investment advice.</span>
      </div>
    </section>
  )
}
